import React, { LegacyRef, forwardRef, useState, useRef, useEffect } from "react";

type InputProps = {
  label?: string;
  helperText?: string;
  error?: boolean;
  containerClass?: string;
  containerRef?: LegacyRef<HTMLDivElement>;
  maxVisibleItems?: number;
} & React.SelectHTMLAttributes<HTMLSelectElement>;

type ParsedOption = {
  value: string | number;
  label: React.ReactNode;
  disabled?: boolean;
};

const extractOptions = (nodes: React.ReactNode): ParsedOption[] => {
  const result: ParsedOption[] = [];
  React.Children.forEach(nodes, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      result.push(...extractOptions(child.props.children));
    } else if (child.type === "option") {
      const p = child.props as React.OptionHTMLAttributes<HTMLOptionElement>;
      const val =
        p.value !== undefined
          ? Array.isArray(p.value)
            ? p.value.join(",")
            : (p.value as string | number)
          : typeof p.children === "string"
          ? p.children
          : "";
      result.push({
        value: val,
        label: p.children,
        disabled: p.disabled,
      });
    }
  });
  return result;
};

const Select = forwardRef<HTMLSelectElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      containerClass,
      children,
      containerRef,
      maxVisibleItems = 7,
      onFocus,
      onBlur,
      onChange,
      value: propsValue,
      defaultValue,
      disabled,
      className,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [internalValue, setInternalValue] = useState<string | number>(
      propsValue !== undefined ? (propsValue as string | number) : (defaultValue as string | number) || ""
    );

    const nativeSelectRef = useRef<HTMLSelectElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const parsedOptions = extractOptions(children);

    useEffect(() => {
      if (propsValue !== undefined) {
        setInternalValue(propsValue as string | number);
      } else if (nativeSelectRef.current) {
        const currentNativeVal = nativeSelectRef.current.value;
        if (currentNativeVal !== undefined && currentNativeVal !== null) {
          setInternalValue(currentNativeVal);
        }
      }
    }, [propsValue, children]);

    const setSelectRef = (element: HTMLSelectElement | null) => {
      nativeSelectRef.current = element;
      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLSelectElement | null>).current = element;
      }
    };

    const setWrapperRef = (element: HTMLDivElement | null) => {
      wrapperRef.current = element;
      if (typeof containerRef === "function") {
        containerRef(element);
      } else if (containerRef) {
        (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = element;
      }
    };

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const handleSelectOption = (optValue: string | number) => {
      setInternalValue(optValue);
      setIsOpen(false);

      if (nativeSelectRef.current) {
        const nativeSelect = nativeSelectRef.current;

        const tracker = (nativeSelect as any)._valueTracker;
        if (tracker) {
          tracker.setValue(internalValue);
        }

        const nativeSetter = Object.getOwnPropertyDescriptor(
          window.HTMLSelectElement.prototype,
          "value"
        )?.set;
        if (nativeSetter) {
          nativeSetter.call(nativeSelect, optValue);
        } else {
          nativeSelect.value = String(optValue);
        }
        const event = new Event("change", { bubbles: true });
        nativeSelect.dispatchEvent(event);
      }

      if (onChange) {
        const syntheticEvent = {
          target: { value: String(optValue), name: props.name, id: props.id },
          currentTarget: { value: String(optValue), name: props.name, id: props.id },
        } as unknown as React.ChangeEvent<HTMLSelectElement>;
        onChange(syntheticEvent);
      }
    };

    const selectedOption = parsedOptions.find(
      (opt) => String(opt.value) === String(internalValue) && String(opt.value) !== ""
    );
    const hasSelection = selectedOption !== undefined;

    const displayLabel = hasSelection
      ? selectedOption.label
      : (props.placeholder || parsedOptions.find((opt) => String(opt.value) === "")?.label || parsedOptions[0]?.label || "");

    const visibleOptionsInList = hasSelection
      ? parsedOptions
      : parsedOptions.filter((opt) => String(opt.value) !== "");

    const maxContainerHeight = maxVisibleItems * 48; // 48px per item (h-12)

    return (
      <div className={`relative ${containerClass || ""}`} ref={setWrapperRef}>
        {!!label && (
          <label
            {...(props.id && { htmlFor: props.id })}
            className="mb-2 block font-semibold"
          >
            {label}
            {props.required && <span className="text-red-500">{" *"}</span>}
          </label>
        )}
        <div
          className={`relative border border-gray-400 rounded-lg ${
            disabled ? "bg-gray-200 cursor-not-allowed" : "bg-white cursor-pointer"
          } ${error ? "border-red-500" : ""}`}
        >
          {/* Custom Trigger Display */}
          <div
            className={`${className || ""} flex items-center justify-between h-12 py-3 px-4 rounded-lg select-none`}
            onClick={(e) => {
              if (disabled) return;
              setIsOpen((prev) => !prev);
              if (!isOpen) onFocus?.(e as any);
              else onBlur?.(e as any);
            }}
          >
            <span className="truncate">{displayLabel}</span>
            <svg
              className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Hidden Native Select for standard HTML form compatibility */}
          <select
            {...props}
            ref={setSelectRef}
            disabled={disabled}
            value={internalValue}
            onChange={(e) => {
              setInternalValue(e.target.value);
              onChange?.(e);
            }}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
          >
            {children}
          </select>

          {/* Custom Floating Dropdown Menu */}
          {isOpen && !disabled && (
            <ul
              style={{ maxHeight: `${maxContainerHeight}px` }}
              className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-400 rounded-lg shadow-lg overflow-y-auto z-50 py-1"
            >
              {visibleOptionsInList.map((opt, idx) => {
                const isSelected = String(opt.value) === String(internalValue);
                return (
                  <li
                    key={idx}
                    className={`h-12 flex items-center px-4 cursor-pointer select-none transition-colors ${
                      opt.disabled
                        ? "opacity-50 cursor-not-allowed bg-gray-100"
                        : isSelected
                        ? "bg-blue-100 font-semibold text-blue-900"
                        : "hover:bg-blue-50 text-gray-800"
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (!opt.disabled) {
                        handleSelectOption(opt.value);
                      }
                    }}
                  >
                    {opt.label}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {!!helperText && (
          <p className={`mt-1 text-sm ${error ? "text-red-500" : "text-gray-500"}`}>{helperText}</p>
        )}
      </div>
    );
  }
);

export default Select;

